/**
 * The software library: the installers this MSP can push to a device, and the panel that pushes one
 * to a set of them.
 *
 * The port of `Views/SoftwareLibraryPage.xaml` + `SoftwareLibraryViewModel`. What carries over:
 *
 *   * CREATING, EDITING, UPLOADING AND REMOVING ARE ADMIN-ONLY; INSTALLING IS STAFF. A package is a
 *     command line that runs as SYSTEM wherever it is sent, so authoring one is an administrator's
 *     decision, and installing one an administrator has vetted is not. The XAML showed every button
 *     to everyone and let the server 403 a technician; here those buttons are not offered to a
 *     technician at all, and the sentence explaining why stays at the foot of the page.
 *   * THE URL SEEDS THE FILE NAME, because the last segment of a download link is usually exactly
 *     right - and the file name is not cosmetic: its extension is what decides how the agent launches
 *     the installer. Only an empty file-name box is seeded, so a deliberate name is never overwritten.
 *   * THE DEPLOY PANEL WAITS FOR THE CUSTOMER LIST. Opening it before the list has loaded used to
 *     leave it permanently empty in the XAML.
 *   * THE INSTALL ANSWER NAMES WHAT IT REFUSED. Asking for twenty machines and being told "started"
 *     would hide the three that are archived.
 *
 * Gone with OpenSilver: the `window.__jetsonPackageUpload` poll and the token in a JavaScript string.
 * The upload is `download.ts` `uploadFiles` with real progress.
 */
import type { CheckBox, Container } from 'webwin';
import * as auth from '../api/auth.js';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import {
  ApprovalStatus,
  SoftwareDeploymentLimits,
  SoftwareInstallState,
  SoftwarePackageSource,
  type CustomerDto,
  type DeviceDto,
  type SoftwarePackageDto
} from '../contracts.gen.js';
import { pickFiles, uploadFiles } from '../download.js';
import { join, plural } from '../format.js';
import { isWindowsCaption } from '../hardware.js';
import { ALLOWED_EXTENSIONS, describeExitCodes, fileNameFromUrl } from '../software.js';
import { CardList } from '../ui/CardList.js';
import type { Card } from '../ui/Card.js';
import { chip } from '../ui/Chip.js';
import { confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { StatusLine } from '../ui/StatusLine.js';
import { button, buttonRow, card, checkBox, comboBox, field, fieldRow, fillWith, FONT, GAP, h1, h2, hstack, inlineRow, muted, relayout, scrollPage, splitRow, stackOf, textBox, value, vstack } from '../ui/layout.js';
import type { CrmPage, PageContext } from './page.js';

const SOURCE_NAMES = ['A file I upload', 'A URL the agent fetches'] as const;
const PICK_ONE = 'Pick at least one device.';

const UPLOAD_HINT =
  'Upload the installer after saving. The CRM hashes it, and every agent verifies that hash before running a byte of it. ' +
  "It downloads with the device's own key, so the file is never reachable without one.";
const URL_HINT =
  "The agent downloads this itself, so the address must be reachable from the customer's network. Plain http is allowed - " +
  'an internal software share is a normal source - which is exactly when the SHA-256 is worth filling in.';

function isAdmin(): boolean {
  return auth.currentUser()?.role?.toLowerCase() === 'admin';
}

/** What makes a device a target: approved, not retired, and running the Windows agent. */
function isInstallable(d: DeviceDto): boolean {
  return d.status === ApprovalStatus.Approved && !d.archived && isWindowsCaption(d.os?.caption);
}

export function softwarePage(): CrmPage {
  return {
    mount(host: Container, ctx: PageContext) {
      const admin = isAdmin();
      let editingId = 0;
      let busy = false;
      let customers: CustomerDto[] = [];
      let deployPackage: SoftwarePackageDto | null = null;
      const selected = new Set<number>();
      let deployDevices: DeviceDto[] = [];

      const status = new StatusLine();

      // =================================================================
      //  Header
      // =================================================================

      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, h1('Software library'));
      if (admin) titleRow.controls.add(button('New package', () => beginNew(), true));
      const header = stackOf(
        vstack({ spacing: GAP.xs }),
        titleRow,
        muted(
          'Installers you can push to any device running the Windows agent. The agent downloads each one, checks it, and runs it silently as SYSTEM.'
        )
      );

      // =================================================================
      //  Editor (admin)
      // =================================================================

      const editorTitle = h2('New package');
      const nameBox = textBox();
      const versionBox = textBox();
      const publisherBox = textBox();
      const sourcePicker = comboBox<string>([...SOURCE_NAMES], {
        selectedIndex: 0,
        onSelectionChanged: () => applySource()
      });
      const fileNameBox = textBox();
      const urlBox = textBox({ placeholder: 'https://' });
      urlBox.on('textchanged', () => {
        if (fileNameBox.text.trim()) return;
        const suggested = fileNameFromUrl(urlBox.text);
        if (suggested) fileNameBox.text = suggested;
      });
      const shaBox = textBox({ placeholder: '64 hex characters (optional)' });
      const argumentsBox = textBox({ placeholder: 'e.g. /VERYSILENT /NORESTART' });
      const exitCodesBox = textBox();
      const timeoutBox = textBox();
      const notesBox = textBox();
      const sourceHint = muted('');
      const editorStatus = new StatusLine();

      const urlRows = stackOf(
        vstack({ spacing: GAP.sm }),
        fieldRow(field('URL', urlBox), field('SHA-256', shaBox)),
        muted(
          'The SHA-256 is optional, and the only thing that makes an unattested download safe to run: every agent checks it before executing a byte, and refuses the file if it differs.'
        )
      );

      const editor = card({ visible: false });
      editor.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          editorTitle,
          fieldRow(field('Name', nameBox), field('Version', versionBox)),
          field('Publisher', publisherBox),
          fieldRow(field('Installer', sourcePicker), field('File name', fileNameBox)),
          muted(
            `The file name's extension is what tells the agent how to launch it. Allowed: ${ALLOWED_EXTENSIONS.join('  ')}`
          ),
          urlRows,
          sourceHint,
          fieldRow(field('Silent switches', argumentsBox), field('Success exit codes', exitCodesBox)),
          muted(
            "An MSI defaults to /qn /norestart if you leave the switches blank; an .exe cannot be guessed - Inno Setup wants /VERYSILENT, NSIS wants /S, InstallShield wants /s /v\"/qn\". Check the vendor's documentation, because an installer that stops on a dialog stops on a desktop nobody is watching."
          ),
          fieldRow(field('Give up after (minutes)', timeoutBox), field('Notes', notesBox)),
          stackOf(buttonRow(), button('Save', () => void save(), true), button('Cancel', () => closeEditor())),
          editorStatus.label
        )
      );

      // =================================================================
      //  Deploy panel
      // =================================================================

      const deployTitle = h2('');
      const customerPicker = comboBox<string>([], { onSelectionChanged: () => void loadDeployDevices() });
      const deviceHost = vstack({ spacing: GAP.sm });
      const deviceEmpty = muted('');
      const deployStatus = new StatusLine();
      const installButton = button('Install on the selected devices', () => void install(), true);

      const deployHeading = splitRow({ align: 'center' });
      fillWith(deployHeading, deployTitle);
      deployHeading.controls.add(button('Close', () => closeDeploy()));

      const deploy = card({ visible: false });
      deploy.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          deployHeading,
          field('Customer', customerPicker),
          stackOf(buttonRow(), button('Select all', () => setAll(true)), button('Select none', () => setAll(false))),
          deviceEmpty,
          deviceHost,
          stackOf(buttonRow(), installButton),
          deployStatus.label
        )
      );

      // =================================================================
      //  Filters and list
      // =================================================================

      const searchBox = textBox({ placeholder: 'Name or publisher' });
      searchBox.on('keydown', (e: { key: string }) => {
        if (e.key === 'Enter') void refresh();
      });
      const retiredCheck = checkBox('Include retired', false);
      retiredCheck.on('change', () => void refresh());
      const filters = card();
      filters.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          stackOf(buttonRow({ spacing: GAP.md, align: 'end' }), field('Search', searchBox, { width: 240 }), retiredCheck),
          stackOf(buttonRow(), button('Apply', () => void refresh(), true), button('Reset', () => void reset()))
        )
      );

      const list = new CardList<SoftwarePackageDto>({ key: p => p.id, build: p => buildPackageCard(p) });
      const empty = new EmptyState('');

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          status.label,
          editor,
          deploy,
          filters,
          empty.panel,
          list.panel,
          muted(
            "Creating and editing packages is an administrator's job, because a package is a command line that runs as SYSTEM on every machine it is sent to. Installing one that an administrator has already vetted is not."
          )
        )
      );
      host.controls.add(view);

      // =================================================================
      //  Rendering
      // =================================================================

      function buildPackageCard(p: SoftwarePackageDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(p.displayName, { font: FONT.bold }));
        if (!p.active) heading.controls.add(chip('retired', 'warn'));
        if (p.installCount > 0) heading.controls.add(chip(plural(p.installCount, 'install'), 'neutral'));

        const body = stackOf(vstack({ spacing: GAP.xs }), heading, muted(p.detailText));
        if (p.notes) body.controls.add(muted(p.notes, 'subtle'));
        if (p.hasWarning) body.controls.add(muted(p.warningText, 'warn'));

        const actions = buttonRow();
        if (p.canInstall) actions.controls.add(button('Install…', () => void openDeploy(p), true));
        if (admin && p.isUploadPackage) actions.controls.add(button('Upload file', () => upload(p)));
        if (admin) {
          actions.controls.add(button('Edit', () => beginEdit(p)));
          actions.controls.add(button('Remove', () => void remove(p)));
        }
        if (actions.controls.length > 0) body.controls.add(actions);

        const shell = card();
        shell.controls.add(body);
        return shell;
      }

      function renderDevices(): void {
        deviceHost.controls.clear();
        for (const d of deployDevices) {
          const box: CheckBox = checkBox('', selected.has(d.id));
          box.on('change', () => {
            if (box.checked) selected.add(d.id);
            else selected.delete(d.id);
            clearPickRefusal();
          });
          const row = splitRow({ align: 'center', spacing: GAP.sm });
          row.controls.add(box);
          fillWith(
            row,
            stackOf(
              vstack({ spacing: 0 }),
              value(d.name, { font: FONT.bold }),
              muted(
                join([
                  d.presence?.isOnline ? 'online' : 'offline - will install when it connects',
                  d.os?.caption
                ])
              )
            )
          );
          deviceHost.controls.add(row);
        }
        deviceEmpty.text =
          deployDevices.length > 0
            ? ''
            : customerPicker.selectedIndex < 0
              ? 'Pick a customer to list their devices.'
              : 'This customer has no approved devices running the Windows agent.';
        deviceEmpty.visible = deviceEmpty.text.length > 0;
        relayout(deviceHost);
      }

      // =================================================================
      //  Editor
      // =================================================================

      function applySource(): void {
        const url = sourcePicker.selectedIndex === 1;
        urlRows.visible = url;
        sourceHint.text = url ? URL_HINT : UPLOAD_HINT;
        relayout(editor);
      }

      function beginNew(): void {
        editingId = 0;
        editorTitle.text = 'New package';
        for (const b of [nameBox, versionBox, publisherBox, fileNameBox, urlBox, shaBox, argumentsBox, notesBox]) b.text = '';
        sourcePicker.selectedIndex = 0;
        exitCodesBox.text = describeExitCodes(null);
        timeoutBox.text = String(SoftwareDeploymentLimits.DefaultTimeoutMinutes);
        openEditor();
      }

      function beginEdit(p: SoftwarePackageDto): void {
        editingId = p.id;
        editorTitle.text = `Edit ${p.name}`;
        // LiteDB reads a stored "" back as null, whatever the contract says.
        nameBox.text = p.name ?? '';
        versionBox.text = p.version ?? '';
        publisherBox.text = p.publisher ?? '';
        notesBox.text = p.notes ?? '';
        sourcePicker.selectedIndex = p.source === SoftwarePackageSource.Url ? 1 : 0;
        // The file name first, so filling the URL box does not "seed" over it.
        fileNameBox.text = p.fileName ?? '';
        urlBox.text = p.downloadUrl ?? '';
        shaBox.text = p.sha256 ?? '';
        argumentsBox.text = p.arguments ?? '';
        exitCodesBox.text = describeExitCodes(p.successExitCodes);
        timeoutBox.text = String(p.timeoutMinutes);
        openEditor();
      }

      function openEditor(): void {
        closeDeploy();
        editorStatus.clear();
        applySource();
        editor.visible = true;
        relayout(content);
        nameBox.focus();
      }

      function closeEditor(): void {
        editor.visible = false;
        relayout(content);
      }

      async function save(): Promise<void> {
        if (busy) return;
        const source = sourcePicker.selectedIndex === 1 ? SoftwarePackageSource.Url : SoftwarePackageSource.Upload;
        const timeout = Number.parseInt(timeoutBox.text.trim(), 10);
        // The server validates and clamps everything; this form only refuses what it can be sure of.
        if (!nameBox.text.trim()) {
          editorStatus.error('A package needs a name.');
          return;
        }
        const body = {
          name: nameBox.text.trim(),
          version: versionBox.text.trim(),
          publisher: publisherBox.text.trim(),
          notes: notesBox.text.trim(),
          source,
          downloadUrl: source === SoftwarePackageSource.Url ? urlBox.text.trim() : '',
          fileName: fileNameBox.text.trim(),
          sha256: source === SoftwarePackageSource.Url ? shaBox.text.trim() : '',
          arguments: argumentsBox.text.trim(),
          successExitCodes: exitCodesBox.text.trim(),
          timeoutMinutes: Number.isFinite(timeout) ? timeout : 0
        };
        busy = true;
        editorStatus.busy('Saving…');
        try {
          const saved =
            editingId === 0
              ? await crm.createSoftwarePackage(body)
              : // null keeps the stored active flag: retiring is Remove's job, not the form's.
                await crm.updateSoftwarePackage(editingId, { ...body, active: null });
          if (ctx.cancelled()) return;
          status.ok(
            editingId === 0 && saved.source === SoftwarePackageSource.Upload
              ? `Saved ${saved.name}. Now upload its installer file - until then it can't be installed.`
              : `Saved ${saved.name}.`
          );
          closeEditor();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) editorStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function remove(p: SoftwarePackageDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Remove package',
          message: `Remove ${p.name} from the library?\n\nDevices it has already been installed on are not touched - this only stops it being installed again.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        status.busy(`Removing ${p.name}…`);
        try {
          const result = await crm.deleteSoftwarePackage(p.id);
          if (ctx.cancelled()) return;
          // A retirement is a success that has to be explained: the row stays in "include retired".
          status.ok(result.removed ? `Removed ${p.name}.` : result.message);
          if (editingId === p.id) closeEditor();
          if (deployPackage?.id === p.id) closeDeploy();
          await refresh();
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      /**
       * Picks and uploads the installer. `pickFiles` runs before any await, inside the click - a file
       * picker opened later has lost the click's activation and the browser refuses it.
       */
      function upload(p: SoftwarePackageDto): void {
        const picked = pickFiles(false, ALLOWED_EXTENSIONS.join(','));
        void (async () => {
          const files = await picked;
          const file = files[0];
          if (!file || ctx.cancelled()) return;
          status.busy(`Uploading ${file.name}…`);
          try {
            await uploadFiles(crm.softwarePackageFilePath(p.id), [file], fraction => {
              if (!ctx.cancelled()) status.busy(`Uploading ${file.name}… ${Math.round(fraction * 100)}%`);
            });
            if (ctx.cancelled()) return;
            status.ok(`Installer uploaded for ${p.name} (${file.name}).`);
            await refresh();
          } catch (error) {
            if (!ctx.cancelled()) status.error(describeError(error));
          }
        })();
      }

      // =================================================================
      //  Deploying
      // =================================================================

      async function openDeploy(p: SoftwarePackageDto): Promise<void> {
        closeEditor();
        deployPackage = p;
        deployTitle.text = `Install ${p.name}`;
        deployStatus.clear();
        deploy.visible = true;
        relayout(content);
        await customersLoaded;
        if (ctx.cancelled()) return;
        if (customerPicker.selectedIndex < 0 && customers.length > 0) customerPicker.selectedIndex = 0;
        else await loadDeployDevices();
      }

      function closeDeploy(): void {
        deployPackage = null;
        deploy.visible = false;
        relayout(content);
      }

      async function loadDeployDevices(): Promise<void> {
        const customer = customers[customerPicker.selectedIndex];
        clearPickRefusal();
        selected.clear();
        deployDevices = [];
        if (!customer || !deploy.visible) {
          renderDevices();
          return;
        }
        try {
          const devices = await crm.getDevices(customer.id, ctx.dispose.signal());
          if (ctx.cancelled() || customers[customerPicker.selectedIndex] !== customer) return;
          deployDevices = devices
            .filter(isInstallable)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
        } catch (error) {
          if (!ctx.cancelled()) deployStatus.error('Could not load devices: ' + describeError(error));
        }
        renderDevices();
      }

      /** "Pick at least one device." describes the selection, so a change to it retires the sentence. */
      function clearPickRefusal(): void {
        if (deployStatus.text === PICK_ONE) deployStatus.clear();
      }

      function setAll(on: boolean): void {
        clearPickRefusal();
        selected.clear();
        if (on) for (const d of deployDevices) selected.add(d.id);
        renderDevices();
      }

      async function install(): Promise<void> {
        const p = deployPackage;
        if (!p || busy) return;
        const ids = deployDevices.filter(d => selected.has(d.id)).map(d => d.id);
        if (ids.length === 0) {
          deployStatus.error(PICK_ONE);
          return;
        }
        const ok = await confirm(ctx.desktop, {
          title: 'Install software',
          message: `Install ${p.name} on ${plural(ids.length, 'device')}?\n\nThe installer runs silently as SYSTEM on each machine. Devices that are offline pick it up the next time they connect.`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        deployStatus.busy(`Starting ${p.name} on ${plural(ids.length, 'device')}…`);
        try {
          const result = await crm.installSoftware(p.id, ids);
          if (ctx.cancelled()) return;
          const queued = result.jobs.filter(j => j.state === SoftwareInstallState.Queued).length;
          let line = `Started on ${plural(result.jobs.length - queued, 'device')}`;
          if (queued > 0) line += `, queued on ${queued} that ${queued === 1 ? 'is' : 'are'} offline`;
          line += ". Watch each device's Software tab for progress.";
          // The refusals are the half a bare "started" would hide.
          if (result.skipped.length > 0) line += `\nSkipped: ${result.skipped.join('; ')}`;
          deployStatus.ok(line);
          relayout(deploy);
        } catch (error) {
          if (!ctx.cancelled()) deployStatus.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      // =================================================================
      //  Loading
      // =================================================================

      async function reset(): Promise<void> {
        searchBox.text = '';
        retiredCheck.checked = false;
        await refresh();
      }

      async function refresh(): Promise<void> {
        try {
          const packages = await crm.getSoftwarePackages(retiredCheck.checked, ctx.dispose.signal(), searchBox.text);
          if (ctx.cancelled()) return;
          list.set(packages);
          const filtered = searchBox.text.trim().length > 0 || retiredCheck.checked;
          empty.message = filtered
            ? 'Nothing matches that filter.'
            : admin
              ? "No packages yet. Add an installer - upload the file, or point at the vendor's download URL - together with the switches that make it run silently, and it can be pushed to any device running the Windows agent."
              : 'No packages yet. An administrator adds installers here; once one is, it can be pushed to any device running the Windows agent.';
          empty.update(packages.length);
          relayout(content);
        } catch (error) {
          if (!ctx.cancelled()) status.error('Could not load the library: ' + describeError(error));
        }
      }

      async function loadCustomers(): Promise<void> {
        try {
          customers = await crm.getCustomers(ctx.dispose.signal());
        } catch {
          customers = []; // the library still works; the deploy panel says there is nothing to pick
        }
        if (ctx.cancelled()) return;
        customerPicker.items = customers.map(c => c.name);
      }

      const customersLoaded = loadCustomers();
      void refresh();
    }
  };
}
